import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { LOGO_URL } from "../lib/brand";
import { KeyRound, Loader2 } from "lucide-react";

/**
 * Full-screen forced password change for bulk-imported students on first login (SEC-006).
 * Blocks all other UI until the temp password is replaced.
 */
export default function MustChangePassword() {
  const { user, setUser, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setUser({ ...user, must_change_password: false });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="must-change-password-screen" className="min-h-screen bg-[var(--bg-deep)] text-[var(--gold-soft)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="CFCS" className="h-14 w-auto rounded-md" />
        </div>
        <div className="nb-card-static p-7">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full bg-[var(--gold)]/15 border-2 border-[var(--gold)] flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[var(--gold)]" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-[var(--cream)]">Set a new password</h1>
              <p className="text-xs text-[var(--muted-text)] mt-0.5">Your instructor issued you a temporary one — please pick your own.</p>
            </div>
          </div>

          {done ? (
            <div data-testid="mcp-success" className="text-center py-6">
              <p className="text-[var(--gold)] font-semibold mb-4">All set — your new password is active.</p>
              <a href="/dashboard" className="nb-btn nb-btn-yellow inline-block">Continue to dashboard</a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Temporary password</label>
                <input
                  data-testid="mcp-current"
                  type="password"
                  required
                  autoFocus
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  className="nb-input"
                  placeholder="From your instructor's email"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">New password</label>
                <input
                  data-testid="mcp-new"
                  type="password"
                  required
                  minLength={8}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  className="nb-input"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Confirm new password</label>
                <input
                  data-testid="mcp-confirm"
                  type="password"
                  required
                  minLength={8}
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="nb-input"
                  placeholder="Type it again"
                />
              </div>
              {error && <div data-testid="mcp-error" className="text-sm text-red-400">{error}</div>}
              <button
                data-testid="mcp-submit"
                disabled={submitting}
                type="submit"
                className="nb-btn nb-btn-yellow w-full flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Updating..." : "Set new password"}
              </button>
              <button
                type="button"
                data-testid="mcp-logout"
                onClick={logout}
                className="w-full text-xs text-[var(--muted-text)] hover:text-[var(--gold)] mt-2"
              >
                Sign out and try later
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
